import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { formatDate, formatTime } from '@app/helpers/formatDateTime.helper';
import { connectionToStatus } from '@app/helpers/status.helper';
import { LegalEntityWithUser } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { DecisionCellRendererComponent } from '@app/ui-components/grid-table/decision-cell-renderer/decision-cell-renderer.component';
import { DeleteCellRendererComponent } from '@app/ui-components/grid-table/delete-cell-renderer/delete-cell-renderer.component';
import { DownloadCellRendererComponent } from '@app/ui-components/grid-table/download-cell-renderer/download-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
} from 'ag-grid-community';
import { map, tap } from 'rxjs';

type Row = {
  id: string;
  date: string;
  createdDate: string;
  createdTime: string;
  title: string;
  subtitle: string;
  documentId?: string;
  docStatus?: 'pending' | 'approved' | 'rejected';
  enabledConnection?: boolean | null;
};
export interface ApprovalRequest {
  id: string;
  userId?: string;
  userEmail?: string | null;
  requestedAt?: string;
  isApproved?: boolean | null;
  status?: string | null;
  [key: string]: unknown;
}

@Component({
  selector: 'app-representatives-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, GridTableComponent],
  templateUrl: './representatives-tab.component.html',
  styleUrl: './representatives-tab.component.scss',
})
export class RepresentativesTabComponent implements OnInit {
  @Input({ required: true }) investorId!: string;

  columns: ColDef[] = [];
  rowModelType: 'infinite' = 'infinite';
  cacheBlockSize = 10;
  maxBlocksInCache = 5;
  rowHeight = 56;
  datasource!: IDatasource;

  private readonly legalEntityService = inject(LegalEntityService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchTerm = signal<string>('');
  readonly isLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
    if (this.investorId) this.datasource = this.buildDatasource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['legalEntityId'] && this.investorId) {
      this.datasource = this.buildDatasource();
    }
  }

  onSearch(term: string | undefined): void {
    this.searchTerm.set(term ?? '');
    this.datasource = this.buildDatasource();
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'date',
        headerName: this.translate.instant('admin.legalEntities.table.date'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.createdDate ?? '-',
          subtitle: param.data?.createdTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 100,
        flex: 0,
        suppressNavigable: true,
        editable: false,
      },
      {
        field: 'user',
        headerName: this.translate.instant('admin.legalEntities.table.user'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.title ?? '',
          subtitle: param.data?.subtitle ?? '',
        }),
        cellStyle: customRendererCellStyle,
      },
      {
        field: 'documents',
        headerName: this.translate.instant(
          'admin.legalEntities.table.documents'
        ),
        cellRenderer: DownloadCellRendererComponent,
        maxWidth: 140,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellClass: 'text-center',
      },
      {
        field: 'decision',
        headerName: '',
        cellRenderer: DecisionCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          status: connectionToStatus(param.data?.enabledConnection ?? null),
          actions: {
            approve: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, true).pipe(
                tap(() => {
                  param.data!.enabledConnection = true;
                  param.api?.refreshCells({
                    force: true,
                    rowNodes: [param.node],
                  });
                })
              ),
            decline: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, false).pipe(
                tap(() => {
                  param.data!.enabledConnection = false;
                  param.api?.refreshCells({
                    force: true,
                    rowNodes: [param.node],
                  });
                })
              ),
          },
        }),
        width: 150,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: '8px',
        },
        cellClass: 'text-center',
      },
      {
        field: 'delete',
        headerName: '',
        cellRenderer: DeleteCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          status: connectionToStatus(param.data?.enabledConnection ?? null),
          onDelete: (rowId: string) =>
            this.legalEntityService.deleteApprovalRequest(rowId).pipe(
              tap(() => {
                if (param.api?.purgeInfiniteCache) {
                  param.api.purgeInfiniteCache();
                } else if (param.api?.refreshInfiniteCache) {
                  param.api.refreshInfiniteCache();
                }
              })
            ),
        }),
        width: 70,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellClass: 'text-center',
      },
    ];
  }

  private buildDatasource(): IDatasource {
    return {
      rowCount: undefined,
      getRows: (params: IGetRowsParams) => {
        const investorId = this.investorId;
        if (!investorId) return params.successCallback([], 0);

        this.isLoading.set(true);
        const search = this.searchTerm().trim().toLowerCase();

        this.legalEntityService
          .getApprovalRequestsByLegalEntity(investorId)
          .pipe(
            map((list: unknown): LegalEntityWithUser[] =>
              Array.isArray(list) ? (list as LegalEntityWithUser[]) : []
            ),
            map((requestList): Row[] =>
              requestList.map((request) => {
                const user = request.user ?? null;
                const personal = user?.personalData ?? null;

                const fullName = [
                  personal?.firstName,
                  personal?.midName,
                  personal?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
                  .trim();

                const email = user?.email ?? personal?.email ?? '';
                const title = fullName ?? '-';
                const subtitle = email ?? '';

                const date = request.decisionDate ?? request.creationDate ?? '';

                return {
                  id: request.id,
                  date: date ?? '',
                  createdDate: formatDate(date ?? ''),
                  createdTime: formatTime(date ?? ''),
                  title,
                  subtitle,
                  documentId: user?.verifDocumentId ?? undefined,
                  docStatus: 'pending',
                  enabledConnection: request.enabledConnection ?? null,
                };
              })
            ),

            map((rows) =>
              search
                ? rows.filter((row) =>
                    [
                      row.date,
                      row.createdDate,
                      row.createdTime,
                      row.title,
                      row.subtitle,
                      row.documentId ?? '',
                    ]
                      .join(' ')
                      .toLowerCase()
                      .includes(search)
                  )
                : rows
            )
          )
          .subscribe({
            next: (rows) => params.successCallback(rows, rows.length),
            error: () => params.successCallback([], 0),
            complete: () => this.isLoading.set(false),
          });
      },
    };
  }
}
